import { useEffect, useState } from 'react';
import { CheckCircle2, Clock, FileDown, ShieldCheck, Sparkles } from 'lucide-react';
import { useBranding } from '../branding/BrandingProvider';
import { UploadArea } from '../components/UploadArea';
import { api, type HistoryEntry } from '../lib/api';
import { formatDateTime } from '../lib/format';

const FALLBACK_COLUMNS = [
  'Nº',
  'Stakeholder',
  'Esfera',
  'Categoria',
  'Empresa/Instituição',
  'Cargo',
  'Valor Posicionamento',
  'Valor Poder',
  'Contexto do Ambiente',
  'Valor Ação - Forma de Atuação',
  'Valor Interesses - Tratamento dos Interesses',
  'Poder X Interesses',
  'Prioridade',
];

interface Props {
  onUpload: (file: File) => void;
  busy: boolean;
  error: string | null;
  onOpenReport: (reportId: string) => void;
}

export function HomeScreen({ onUpload, busy, error, onOpenReport }: Props): JSX.Element {
  const branding = useBranding();
  const [columns, setColumns] = useState<string[]>(FALLBACK_COLUMNS);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    let active = true;
    void api
      .getColumnsMetadata()
      .then((metadata) => {
        if (active && metadata.expectedColumns.length > 0) setColumns(metadata.expectedColumns);
      })
      .catch(() => undefined);
    void api
      .listHistory()
      .then((entries) => {
        if (active) setHistory(entries);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="mx-auto grid max-w-[1400px] gap-8 px-6 py-10 lg:grid-cols-[1.05fr_1fr]">
      <section className="space-y-6">
        <div>
          <p className="eyebrow">{branding.appName}</p>
          <h1 className="mt-2 text-4xl font-semibold leading-tight tracking-tight text-brand-ink">
            {branding.productName}
          </h1>
          <p className="mt-4 max-w-xl text-base text-brand-slate">
            Envie a planilha de Stakeholders, confira os dados importados e gere os infográficos executivos com
            exportação em PDF — sem depender de diagramação externa.
          </p>
        </div>

        <ul className="grid gap-3 sm:grid-cols-2">
          {[
            { icon: CheckCircle2, text: 'Validação das colunas obrigatórias antes de qualquer cálculo' },
            { icon: ShieldCheck, text: 'Processamento determinístico: nenhum dado é inventado' },
            { icon: Sparkles, text: 'Infográficos isométricos vetoriais com identidade Integratio' },
            { icon: FileDown, text: 'PDF completo com capa ou exportação por gráfico' },
          ].map(({ icon: Icon, text }) => (
            <li key={text} className="card flex items-start gap-3 p-4 text-sm text-brand-graphite">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" aria-hidden />
              {text}
            </li>
          ))}
        </ul>

        <div className="card p-6">
          <h2 className="text-sm font-semibold text-brand-ink">Colunas esperadas na planilha</h2>
          <p className="mt-1 text-xs text-brand-slate">
            O sistema localiza automaticamente a linha de cabeçalho e aceita variações de acentuação e espaços.
          </p>
          <ol className="mt-4 grid gap-2 text-xs text-brand-graphite sm:grid-cols-2">
            {columns.map((column, index) => (
              <li key={column} className="flex items-center gap-2 rounded-lg bg-brand-mist px-3 py-2">
                <span className="text-[10px] font-bold text-brand-slate">{String(index + 1).padStart(2, '0')}</span>
                {column}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-brand-ink">Enviar planilha de Stakeholders</h2>
          <p className="mt-1 text-sm text-brand-slate">Arquivo .xlsx de até 10 MB. Nada é gravado na planilha original.</p>
          <div className="mt-5">
            <UploadArea onSubmit={onUpload} busy={busy} />
          </div>
          {error && (
            <p role="alert" className="mt-4 rounded-xl border border-brand-red/30 bg-brand-red/5 px-4 py-3 text-sm text-brand-redDark">
              {error}
            </p>
          )}
        </div>

        {history.length > 0 && (
          <div className="card p-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
              <Clock className="h-4 w-4 text-brand-slate" aria-hidden />
              Relatórios gerados nesta sessão do servidor
            </h2>
            <ul className="mt-4 divide-y divide-brand-line text-sm">
              {history.map((entry) => (
                <li key={entry.reportId} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-medium text-brand-ink">{entry.fileName}</p>
                    <p className="text-xs text-brand-slate">
                      {formatDateTime(entry.generatedAt)} · {entry.totalStakeholders} stakeholders · {entry.user}
                    </p>
                  </div>
                  <button type="button" className="btn-secondary" onClick={() => onOpenReport(entry.reportId)}>
                    Abrir relatório
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
